/**
 * A whole page palette derived from one colour.
 *
 * The brief's rule is that an owner never types a colour into a block, and that
 * stays true: a row still picks a ROLE (`surface`, `muted`, `accent`) and the
 * roles are filled from here. What changes is where the freedom lives. Five
 * preset palettes meant five looks for every restaurant in the country; one
 * brand colour means every venue is its own while nothing inside the page can
 * clash, because every value below comes from the same hue.
 *
 * The contrast loops are the reason this is safe to hand to somebody with no
 * design training. Whatever colour they pick, the text is darkened until it
 * clears WCAG AA against every surface it can land on. A pale yellow brand and
 * a navy one both end up readable; they just end up readable differently.
 */

/** The colour roles a page has, in the order the builder lists them. */
export const PALETTE_ROLES = [
  'bg',
  'accent',
  'text',
  'textMuted',
  'primary',
  'onPrimary',
  'border'
] as const

export type PaletteRole = keyof DerivedPalette

export interface DerivedPalette {
  bg: string
  surface: string
  muted: string
  accent: string
  text: string
  textMuted: string
  primary: string
  onPrimary: string
  border: string
}

interface Rgb {
  r: number
  g: number
  b: number
}

interface Hsl {
  h: number
  s: number
  l: number
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value))

export function hexToRgb(hex: string): Rgb {
  const clean = hex.replace('#', '').trim()
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map(character => character + character)
          .join('')
      : clean
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16)
  }
}

const toHexPair = (value: number) =>
  clamp(Math.round(value), 0, 255).toString(16).padStart(2, '0')

export const rgbToHex = ({ r, g, b }: Rgb) =>
  `#${toHexPair(r)}${toHexPair(g)}${toHexPair(b)}`

export function rgbToHsl({ r, g, b }: Rgb): Hsl {
  const red = r / 255
  const green = g / 255
  const blue = b / 255
  const max = Math.max(red, green, blue)
  const min = Math.min(red, green, blue)
  const l = (max + min) / 2

  if (max === min) return { h: 0, s: 0, l }

  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h: number
  if (max === red) h = ((green - blue) / d + (green < blue ? 6 : 0)) / 6
  else if (max === green) h = ((blue - red) / d + 2) / 6
  else h = ((red - green) / d + 4) / 6

  return { h, s, l }
}

export function hslToRgb({ h, s, l }: Hsl): Rgb {
  if (s === 0) {
    const value = l * 255
    return { r: value, g: value, b: value }
  }

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q

  const channel = (t: number) => {
    let shifted = t
    if (shifted < 0) shifted += 1
    if (shifted > 1) shifted -= 1
    if (shifted < 1 / 6) return p + (q - p) * 6 * shifted
    if (shifted < 1 / 2) return q
    if (shifted < 2 / 3) return p + (q - p) * (2 / 3 - shifted) * 6
    return p
  }

  return {
    r: channel(h + 1 / 3) * 255,
    g: channel(h) * 255,
    b: channel(h - 1 / 3) * 255
  }
}

/** WCAG relative luminance. */
export function relativeLuminance({ r, g, b }: Rgb): number {
  const channel = (value: number) => {
    const v = value / 255
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

/** WCAG contrast ratio, 1 to 21. */
export function contrastRatio(a: string, b: string): number {
  const first = relativeLuminance(hexToRgb(a))
  const second = relativeLuminance(hexToRgb(b))
  const lighter = Math.max(first, second)
  const darker = Math.min(first, second)
  return (lighter + 0.05) / (darker + 0.05)
}

const withLightness = (hsl: Hsl, l: number) =>
  rgbToHex(hslToRgb({ ...hsl, l: clamp(l, 0, 1) }))

/**
 * Darkens a colour, one percent of lightness at a time, until it clears `target`
 * against every background it will be drawn on.
 *
 * A loop rather than a formula because the relationship between lightness and
 * contrast is not linear and depends on the hue: the same step buys far more
 * contrast in blue than in yellow.
 */
function darkenUntilReadable(
  hsl: Hsl,
  startLightness: number,
  backgrounds: string[],
  target: number
): string {
  const readable = (candidate: string) =>
    backgrounds.every(background => contrastRatio(candidate, background) >= target)

  let lightness = startLightness
  let candidate = withLightness(hsl, lightness)

  while (lightness > 0 && !readable(candidate)) {
    lightness -= 0.01
    candidate = withLightness(hsl, lightness)
  }

  return candidate
}

export function derivePalette(brandColor: string): DerivedPalette {
  const brandHsl = rgbToHsl(hexToRgb(brandColor))

  /*
    Two levels of tint, not one.

    The page itself stays almost colourless: a background carrying the full
    chroma of a strong brand is a page nobody reads past the first paragraph.
    A highlighted band is the opposite problem. With the same faint saturation
    it came out at a contrast of 1.19 against the page, which is to say
    invisible: three "different" backgrounds that measured 1.15 to 1.40 apart
    and were, correctly, described as all the same colour.
  */
  const paper: Hsl = { h: brandHsl.h, s: clamp(brandHsl.s * 0.12, 0, 0.12), l: 0 }
  const band: Hsl = { h: brandHsl.h, s: clamp(brandHsl.s * 0.8, 0, 0.6), l: 0 }

  const bg = withLightness(paper, 0.975)
  const surface = withLightness(paper, 0.995)
  // Kept for documents written before the picker offered two options.
  const muted = withLightness(band, 0.88)
  const accent = withLightness(band, 0.8)
  const border = withLightness(band, 0.86)

  // Text has to clear AA on every surface a row can be given, not just on the
  // page background, because a row can be `muted` or `accent`.
  const surfaces = [bg, surface, muted, accent]
  const inkHue: Hsl = { h: brandHsl.h, s: clamp(brandHsl.s * 0.5, 0, 0.4), l: 0 }

  const text = darkenUntilReadable(inkHue, 0.22, surfaces, 7)
  const textMuted = darkenUntilReadable(inkHue, 0.46, surfaces, 4.5)

  const primary = rgbToHex(hexToRgb(brandColor))
  const onPrimary =
    contrastRatio('#ffffff', primary) >= contrastRatio('#000000', primary)
      ? '#ffffff'
      : '#000000'

  return { bg, surface, muted, accent, text, textMuted, primary, onPrimary, border }
}

/** The palette as the custom properties `site.scss` reads. */
export function paletteToCssVariables(
  palette: DerivedPalette
): Record<string, string> {
  return {
    '--mp-bg': palette.bg,
    '--mp-surface': palette.surface,
    '--mp-muted': palette.muted,
    '--mp-accent': palette.accent,
    '--mp-text': palette.text,
    '--mp-text-muted': palette.textMuted,
    '--mp-primary': palette.primary,
    '--mp-on-primary': palette.onPrimary,
    '--mp-border': palette.border
  }
}

/**
 * The palette a document actually renders with: derived from the brand colour,
 * then each role the builder set individually laid on top. The one place that
 * knows the precedence, used by the renderer and by the builder's swatches so
 * the two cannot disagree.
 */
export function resolvePalette(
  brandColor: string,
  overrides: Partial<DerivedPalette> | undefined
): DerivedPalette {
  return { ...derivePalette(brandColor), ...(overrides ?? {}) }
}
